/**
 * ClinicContext — Manages the active clinic for multi-clinic support
 *
 * - Staff users: locked to their assigned clinic (user.clinicId)
 * - Admin/Doctor users: can switch between clinics or view all
 * - The activeClinicId is used by all data-fetching components to filter
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import API_BASE_URL from '../config/api';

const ClinicContext = createContext(null);

export const useClinic = () => {
  const context = useContext(ClinicContext);
  if (!context) throw new Error('useClinic must be used within a ClinicProvider');
  return context;
};

export const ClinicProvider = ({ children }) => {
  const { user } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [activeClinicId, setActiveClinicId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Can the user switch clinics?
  const canSwitch = user?.role === 'admin' || user?.role === 'doctor';

  // Fetch available clinics
  const fetchClinics = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API_BASE_URL}/clinics/my-clinics`);
      const active = res.data || [];
      setClinics(active);

      // Set initial active clinic
      const saved = sessionStorage.getItem('activeClinicId');
      if (canSwitch && saved && active.find(c => c.id === saved)) {
        setActiveClinicId(saved);
      } else if (user.clinicId) {
        setActiveClinicId(user.clinicId);
      } else if (canSwitch && active.length > 0) {
        // Admin with no assigned clinic — default to first clinic
        setActiveClinicId(active[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch clinics:', err);
    } finally {
      setLoading(false);
    }
  }, [user, canSwitch]);

  useEffect(() => { fetchClinics(); }, [fetchClinics]);

  // Switch clinic (admin/doctor only)
  const switchClinic = (clinicId) => {
    if (!canSwitch) return;
    setActiveClinicId(clinicId);
    sessionStorage.setItem('activeClinicId', clinicId);
  };

  // Get the active clinic object
  const activeClinic = clinics.find(c => c.id === activeClinicId) || null;

  // Query param to append to API calls
  const clinicParam = activeClinicId ? `clinicId=${activeClinicId}` : '';

  const value = {
    clinics,           // All available clinics
    activeClinicId,    // Currently selected clinic ID
    activeClinic,      // Currently selected clinic object
    canSwitch,         // Whether user can switch clinics
    switchClinic,      // Function to switch
    clinicParam,       // Query string param for API calls
    loading
  };

  return (
    <ClinicContext.Provider value={value}>
      {children}
    </ClinicContext.Provider>
  );
};

export default ClinicContext;
